/**
 * Generates a stable SHA-256 hash of non-sensitive browser signals.
 *
 * Only the hash is ever transmitted — raw signals are never sent to the server.
 * The hash is sent as the X-Device-Fingerprint request header on all auth
 * token-issuing endpoints and on every token refresh. The server stores it at
 * session creation and validates it on every /auth/refresh call.
 *
 * A mismatch (e.g. stolen refresh cookie replayed from a different machine)
 * causes the server to reject the refresh with SESSION_DEVICE_MISMATCH,
 * forcing full re-authentication.
 */

let cachedFingerprint: string | null = null

export async function getDeviceFingerprint(): Promise<string> {
  if (cachedFingerprint) return cachedFingerprint

  const signals = [
    navigator.userAgent,
    String(screen.width),
    String(screen.height),
    String(screen.colorDepth),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    String(navigator.hardwareConcurrency ?? 0),
  ].join('|')

  const encoder = new TextEncoder()
  const data = encoder.encode(signals)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  cachedFingerprint = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return cachedFingerprint
}
